import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  Stack,
  TextField,
  Snackbar,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, ArrowForward, CheckCircle, Email } from '@mui/icons-material';

const CTASection = ({ title, subtitle, buttonText, buttonLink, variant = 'default' }) => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const ref = React.useRef();
  const isInView = useInView(ref, { once: true });

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 3000);
    }
  };

  if (variant === 'newsletter') {
    return (
      <Box
        ref={ref}
        sx={{
          py: 10,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 6s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 8s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Chip
                label="Stay Updated"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {title || 'Subscribe to Our Newsletter'}
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', maxWidth: 500 }}>
                {subtitle || 'Get the latest news and updates directly in your inbox'}
              </Typography>
              
              <Box component="form" onSubmit={handleSubscribe} sx={{ width: '100%', maxWidth: 500, mt: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'transparent',
                        },
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    endIcon={<Send />}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: 'white',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Subscribe
                  </Button>
                </Stack>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                No spam, unsubscribe anytime.
              </Typography>
            </Stack>
          </motion.div>
        </Container>

        <Snackbar open={submitted} autoHideDuration={3000} onClose={() => setSubmitted(false)}>
          <Alert severity="success" sx={{ width: '100%' }}>
            Successfully subscribed to our newsletter!
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  if (variant === 'contact') {
    return (
      <Box
        ref={ref}
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Paper
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                textAlign: 'center',
                background: `linear-gradient(135deg, white 0%, ${theme.palette.grey[50]} 100%)`,
              }}
              elevation={0}
            >
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {title || 'Need Help? Contact Our Team'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                {subtitle || 'Our experts are ready to assist you 24/7'}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  component={Link}
                  to={buttonLink || '/contact'}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{ px: 4 }}
                >
                  {buttonText || 'Contact Support'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Email />}
                  href="mailto:support@globalcomm.com"
                  sx={{ px: 4 }}
                >
                  Email Us
                </Button>
              </Stack>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // Default CTA Variant
  return (
    <Box
      ref={ref}
      sx={{
        py: 12,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '80%',
          height: '80%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite',
        }}
      />
      
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <Stack spacing={4} alignItems="center" textAlign="center">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <CheckCircle sx={{ fontSize: 64, color: 'rgba(255,255,255,0.9)' }} />
            </motion.div>
            
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              {title}
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 600,
              }}
            >
              {subtitle}
            </Typography>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                component={Link}
                to={buttonLink}
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {buttonText}
              </Button>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CTASection;